"use client";

import { useState, useEffect } from "react";
import { CheckCircle, X } from "lucide-react";

interface PurchaseNotification {
  id: number;
  name: string;
  product: string;
  city: string;
  timeAgo: string;
}

const firstNames = [
  "João", "Maria", "Pedro", "Ana", "Carlos", "Juliana", "Lucas", "Beatriz",
  "Rafael", "Camila", "Felipe", "Amanda", "Bruno", "Larissa", "Gustavo",
  "Fernanda", "Matheus", "Gabriela", "Thiago", "Mariana", "Diego", "Patricia",
  "Rodrigo", "Bruna", "Vinicius", "Leticia", "André", "Carla", "Fernando",
  "Aline", "Marcelo", "Tatiana", "Ricardo", "Renata", "Daniel", "Vanessa",
  "Leandro", "Priscila", "Fabio", "Claudia", "Eduardo", "Sandra", "Roberto",
  "Monica", "Alexandre", "Cristina", "Paulo", "Adriana", "Sergio", "Daniela"
];

const lastNames = [
  "Silva", "Santos", "Oliveira", "Souza", "Rodrigues", "Ferreira", "Alves",
  "Pereira", "Lima", "Gomes", "Costa", "Ribeiro", "Martins", "Carvalho",
  "Rocha", "Almeida", "Nascimento", "Araújo", "Melo", "Barbosa", "Cardoso",
  "Reis", "Castro", "Andrade", "Mendes", "Cavalcanti", "Dias", "Moreira",
  "Teixeira", "Monteiro", "Lopes", "Freitas", "Correia", "Barros", "Moura"
];

const cities = [
  "São Paulo - SP", "Rio de Janeiro - RJ", "Belo Horizonte - MG", "Brasília - DF",
  "Salvador - BA", "Fortaleza - CE", "Curitiba - PR", "Recife - PE",
  "Porto Alegre - RS", "Manaus - AM", "Belém - PA", "Goiânia - GO",
  "Guarulhos - SP", "Campinas - SP", "São Luís - MA", "São Gonçalo - RJ",
  "Maceió - AL", "Duque de Caxias - RJ", "Natal - RN", "Campo Grande - MS",
  "Teresina - PI", "São Bernardo - SP", "João Pessoa - PB", "Santo André - SP",
  "Osasco - SP", "Jaboatão - PE", "Ribeirão Preto - SP", "Uberlândia - MG",
  "Sorocaba - SP", "Contagem - MG", "Aracaju - SE", "Feira de Santana - BA",
  "Cuiabá - MT", "Joinville - SC", "Juiz de Fora - MG", "Londrina - PR"
];

const products = [
  "Método Libertação Das Apostas",
  "Jornada dos 8 Pilares",
  "Pacote 5 Mentorias Individuais",
  "Módulo Completo LDA"
];

const timeOptions = [
  "agora mesmo",
  "1 minuto atrás",
  "2 minutos atrás",
  "3 minutos atrás",
  "5 minutos atrás",
  "há poucos minutos"
];

const getRandomItem = <T,>(array: T[]): T => {
  return array[Math.floor(Math.random() * array.length)];
};

const generateNotification = (id: number): PurchaseNotification => {
  const firstName = getRandomItem(firstNames);
  const lastName = getRandomItem(lastNames);
  const fullName = `${firstName} ${lastName.charAt(0)}.`;
  
  return {
    id,
    name: fullName,
    product: getRandomItem(products),
    city: getRandomItem(cities),
    timeAgo: getRandomItem(timeOptions)
  };
};

export const PurchaseNotifications = () => {
  const [notifications, setNotifications] = useState<PurchaseNotification[]>([]);
  const [nextId, setNextId] = useState(1);

  useEffect(() => {
    // Mostrar primeira notificação após 5 segundos
    const initialTimeout = setTimeout(() => {
      const notification = generateNotification(nextId);
      setNotifications([notification]);
      setNextId(nextId + 1);
    }, 355000);

    return () => clearTimeout(initialTimeout);
  }, []);

  useEffect(() => {
    if (nextId === 1) return; // Não iniciar intervalo até primeira notificação

    // Adicionar nova notificação a cada 15-25 segundos
    const interval = setInterval(() => {
      const notification = generateNotification(nextId);
      setNotifications(prev => [notification, ...prev].slice(0, 1)); // Manter apenas 1
      setNextId(prev => prev + 1);
    }, Math.random() * 10000 + 15000); // 15-25 segundos

    return () => clearInterval(interval);
  }, [nextId]);

  const removeNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Auto-remover após 8 segundos
  useEffect(() => {
    if (notifications.length > 0) {
      const latest = notifications[0];
      const timeout = setTimeout(() => {
        removeNotification(latest.id);
      }, 8000);
      return () => clearTimeout(timeout);
    }
  }, [notifications]);

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2 max-w-[320px] pointer-events-none">
      {notifications.map((notification, index) => (
        <div
          key={notification.id}
          className="pointer-events-auto animate-slide-in-left relative"
          style={{
            animation: "slideInLeft 0.5s ease-out forwards"
          }}
        >
          {/* Ícone de check FORA da div principal */}
          <div className="absolute -top-2 -left-2 w-9 h-9 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/40 border-3 border-white z-10">
            <CheckCircle className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>

          {/* Card principal */}
          <div className="ml-3 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-3 pr-8 relative">
              {/* Botão fechar */}
              <button
                onClick={() => removeNotification(notification.id)}
                className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 transition-colors rounded-full hover:bg-slate-100 p-0.5"
                aria-label="Fechar notificação"
              >
                <X className="w-3.5 h-3.5" />
              </button>

              {/* Conteúdo */}
              <div className="pl-3">
                <p className="font-bold text-slate-900 text-sm mb-0.5 leading-tight">
                  {notification.name}
                </p>

                <p className="text-xs text-slate-600 mb-1.5 leading-snug">
                  comprou: <span className="font-semibold text-slate-900">{notification.product}</span>
                </p>

                <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                  <span className="flex items-center gap-0.5">
                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    {notification.city}
                  </span>
                  <span>•</span>
                  <span>{notification.timeAgo}</span>
                </div>
              </div>

              {/* Barra de progresso */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-100">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 animate-progress"
                  style={{
                    animation: "progress 8s linear forwards"
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        .animate-slide-in-left {
          animation: slideInLeft 0.5s ease-out forwards;
        }

        .animate-progress {
          animation: progress 8s linear forwards;
        }
      `}</style>
    </div>
  );
};
